import {
  AuditOutlined,
  BookOutlined,
  CloseCircleOutlined,
  CommentOutlined,
  DoubleRightOutlined,
  FileTextOutlined,
  FormOutlined,
  HighlightOutlined,
  LinkOutlined,
  MailOutlined,
  MinusSquareFilled,
  RetweetOutlined,
  RollbackOutlined,
  SnippetsOutlined,
  UpCircleFilled,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Tooltip } from 'antd';

import { CandidatePhoneContactSerializer } from '../../types/api';

interface ApplicantNavigatorActionsDropdownProps {
  location: 'TALENT_POOL' | 'CANDIDATE_ANALYSIS';
  moveStage?: boolean;
  onMoveStageClick?: () => void;
  reject?: boolean;
  onRejectClick?: () => void;
  rollback?: boolean;
  onRollbackClick?: () => void;
  givenUp?: boolean;
  onGivenUpClick?: () => void;
  select?: boolean;
  onSelectClick?: () => void;
  comment?: boolean;
  onCommentClick?: () => void;
  startInterview?: boolean;
  onStartInterviewClick?: () => void;
  sendEmail?: boolean;
  onSendEmailClick?: () => void;
  sendTests?: boolean;
  onSendTestsClick?: () => void;
  sendCustomTests?: boolean;
  onSendCustomTestsClick?: () => void;
  disableSendAdditionalQuestions?: boolean;
  sendAdditionalQuestions?: boolean;
  onSendAdditionalQuestionsClick?: () => void;
  disableSendSankhyaAdmission?: boolean;
  sendSankhyaAdmission?: boolean;
  onSendSankhyaAdmissionClick?: () => void;
  candidateWhatsApp?: CandidatePhoneContactSerializer;
  sendWhatsApp?: boolean;
  sendPracticalTechnicalCase?: boolean;
  onSendPracticalTechnicalCaseClick?: () => void;
}

const ApplicantNavigatorActionsDropdown = ({
  location,
  moveStage,
  onMoveStageClick,
  reject,
  onRejectClick,
  rollback,
  onRollbackClick,
  givenUp,
  onGivenUpClick,
  comment,
  onCommentClick,
  startInterview,
  onStartInterviewClick,
  select,
  onSelectClick,
  disableSendAdditionalQuestions,
  sendAdditionalQuestions,
  onSendAdditionalQuestionsClick,
  sendCustomTests,
  onSendCustomTestsClick,
  sendEmail,
  onSendEmailClick,
  sendTests,
  onSendTestsClick,
  disableSendSankhyaAdmission,
  sendSankhyaAdmission,
  onSendSankhyaAdmissionClick,
  sendWhatsApp,
  candidateWhatsApp,
  sendPracticalTechnicalCase,
  onSendPracticalTechnicalCaseClick,
}: ApplicantNavigatorActionsDropdownProps) => {
  return (
    <Dropdown
      menu={{
        onClick: ({ key }) => {
          switch (key) {
            case 'stage':
              moveStage && onMoveStageClick?.();
              break;
            case 'reject':
              reject && onRejectClick?.();
              break;
            case 'rollback':
              rollback && onRollbackClick?.();
              break;
            case 'giveup':
              givenUp && onGivenUpClick?.();
              break;
            case 'select':
              select && onSelectClick?.();
              break;
            case 'annotation':
              comment && onCommentClick?.();
              break;
            case 'email':
              sendEmail && onSendEmailClick?.();
              break;
            case 'tests':
              sendTests && onSendTestsClick?.();
              break;
            case 'custom-tests':
              sendCustomTests && onSendCustomTestsClick?.();
              break;
            case 'additional_questions':
              sendAdditionalQuestions && onSendAdditionalQuestionsClick?.();
              break;
            case 'send-sankhya-admission':
              sendSankhyaAdmission && onSendSankhyaAdmissionClick?.();
              break;
            case 'start-interview':
              startInterview && onStartInterviewClick?.();
              break;
            case 'practical-technical-case':
              sendPracticalTechnicalCase && onSendPracticalTechnicalCaseClick?.();
              break;
          }
        },
        items: [
          moveStage
            ? {
                key: 'stage',
                icon: <DoubleRightOutlined />,
                label: 'Mover etapa',
              }
            : null,
          reject ? { key: 'reject', icon: <MinusSquareFilled />, label: 'Reprovar' } : null,
          rollback
            ? {
                key: 'rollback',
                icon: <RollbackOutlined />,
                label: 'Retornar candidato',
              }
            : null,
          moveStage || reject || rollback ? { type: 'divider' as const } : null,
          sendSankhyaAdmission
            ? {
                key: 'send-sankhya-admission',
                icon: <RetweetOutlined />,
                label: (
                  <Tooltip
                    title={disableSendSankhyaAdmission ? 'Já há uma admissão sendo enviada' : ''}
                  >
                    Enviar admissão
                  </Tooltip>
                ),
                disabled: disableSendSankhyaAdmission,
              }
            : null,
          givenUp
            ? {
                key: 'giveup',
                icon: <CloseCircleOutlined />,
                label: 'Marcar desistente',
              }
            : null,
          select
            ? {
                key: 'select',
                icon: <DoubleRightOutlined />,
                label: location === 'CANDIDATE_ANALYSIS' ? 'Adicionar à vaga' : 'Triar para vaga',
              }
            : null,
          comment
            ? { key: 'annotation', icon: <FormOutlined />, label: 'Registrar comentário' }
            : null,
          startInterview
            ? { key: 'start-interview', icon: <HighlightOutlined />, label: 'Iniciar entrevista' }
            : null,
          sendEmail ||
          sendAdditionalQuestions ||
          sendCustomTests ||
          sendTests ||
          sendPracticalTechnicalCase
            ? {
                key: 'send',
                label: 'Enviar e-mail',
                icon: <MailOutlined />,
                children: [
                  sendEmail
                    ? { key: 'email', icon: <CommentOutlined />, label: 'Enviar comunicação' }
                    : null,
                  sendTests
                    ? { key: 'tests', icon: <AuditOutlined />, label: 'Enviar testes' }
                    : null,
                  sendCustomTests
                    ? {
                        key: 'custom-tests',
                        icon: <BookOutlined />,
                        label: 'Enviar testes customizados',
                      }
                    : null,
                  sendAdditionalQuestions
                    ? {
                        key: 'additional_questions',
                        icon: <FileTextOutlined />,
                        disabled: disableSendAdditionalQuestions,
                        label: (
                          <Tooltip
                            title={
                              disableSendAdditionalQuestions
                                ? 'O candidato já respondeu as perguntas de pré-requisito'
                                : ''
                            }
                          >
                            Enviar perguntas de pré-requisito
                          </Tooltip>
                        ),
                      }
                    : null,
                  sendPracticalTechnicalCase
                    ? {
                        key: 'practical-technical-case',
                        icon: <SnippetsOutlined />,
                        label: 'Enviar case',
                      }
                    : null,
                ],
              }
            : null,
          sendWhatsApp
            ? {
                key: 'whatsapp',
                icon: <WhatsAppOutlined />,
                disabled: !candidateWhatsApp,
                label: (
                  <a
                    href={`https://api.whatsapp.com/send?phone=${
                      candidateWhatsApp?.country_code ?? 55
                    }${candidateWhatsApp?.number ?? ''}`}
                    target="_blank"
                  >
                    Enviar WhatsApp <LinkOutlined />
                  </a>
                ),
              }
            : null,
        ],
      }}
    >
      <Button type="primary" icon={<UpCircleFilled />}>
        Ações
      </Button>
    </Dropdown>
  );
};

export default ApplicantNavigatorActionsDropdown;
